import React from 'react';
import { motion } from 'framer-motion';
import { Link2, FileText, TrendingUp } from 'lucide-react';

const steps = [
  {
    number: '01',
    title: 'We Learn Your Business',
    description: 'We hop on a call to understand your lead sources, brand voice, qualifying criteria, and booking preferences.',
    icon: Link2,
  },
  {
    number: '02',
    title: 'We Build Your Custom Agent',
    description: 'Our team builds and fine-tunes a custom AI voice agent tailored to your scripts, tone, and CRM workflow.',
    icon: FileText,
  },
  {
    number: '03',
    title: 'We Deploy & Manage It For You',
    description: 'We handle the full deployment and ongoing management. You just watch the appointments roll into your calendar.',
    icon: TrendingUp,
  },
];

export default function HowItWorks() {
  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="py-24 lg:py-32 relative overflow-hidden"
    >
      {/* Background with Calgary skyline */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1578254457810-0abd1efb0b5f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxjYWxnYXJ5JTIwc2t5bGluZSUyMG5pZ2h0JTIwZGFya3xlbnwwfHx8fDE3NzQ5MzMyNzB8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-black/80" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="mb-20 text-center"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-[#00E5FF] font-semibold block mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            How It Works
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-white"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            We Handle Everything, Start to Finish
          </h2>
        </motion.div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 relative">
          {/* Connector line (desktop) */}
          <div className="hidden md:block absolute top-16 left-[16.67%] right-[16.67%] h-[1px] bg-gradient-to-r from-[#00E5FF]/40 via-[#00E5FF]/20 to-[#00E5FF]/40" />

          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.6 }}
                data-testid={`step-${step.number}`}
                className="text-center relative"
              >
                {/* Step number circle */}
                <div className="w-14 h-14 rounded-full border border-[#00E5FF]/30 bg-[#050505] flex items-center justify-center mx-auto mb-6 relative z-10">
                  <Icon size={22} className="text-[#00E5FF]" />
                </div>

                <span className="text-xs text-[#00E5FF]/60 font-mono tracking-wider block mb-2">
                  Step {step.number}
                </span>
                <h3
                  className="text-xl sm:text-2xl tracking-tight font-medium text-white mb-3"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {step.title}
                </h3>
                <p
                  className="text-base leading-relaxed text-neutral-400 max-w-sm mx-auto"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {step.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
